import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Plus, Trash2, Edit2, Image as ImageIcon, Loader2, Share2, Printer, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getCountryName } from '../../utils/countries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { useDivision7ModalState } from '../../hooks/useDivision7ModalState';
import { useDivision7Filters } from '../shared/useDivision7Filters';
import { Division7FilterBar } from '../shared/Division7FilterBar';
import { ShareSection } from '../../features/shared/ShareSection';
import { AddWeatherBarrierProductWizard } from './AddWeatherBarrierProductWizard';
import { QRThumbnail, buildQrUrl } from '../shared/PrintComponents';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { OrangeCheckbox, QRPreviewModal } from '../shared/WizardComponents';
import { generatePdfFromElement, generatePdfFromModalContent, buildPdfFilename } from '../../utils/pdfExport';

export const WeatherBarriersSection = ({
  canEditCSIItems, canManageProject, companyId, projectId,
  csiItem, project, user,
  shareCompanySearch, showCompanySuggestions, companySuggestions,
  highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown,
  setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, productName: '' });

  // Print
  const [viewProduct, setViewProduct] = useState(null);
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const [pdfProduct, setPdfProduct] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const pdfRef = useRef(null);
  const { startDownload } = useDownload();

  const { urlModal, urlProductId, openModal, closeModal } = useDivision7ModalState('wb');
  const { filtered, filters, setFilter, clearFilters, filterOptions, schema, activeCount } = useDivision7Filters('weather-barriers', products);
  const isUserActionRef = useRef(false);

  // URL → modal sync (restore on refresh / back-forward)
  useEffect(() => {
    if (loading || isUserActionRef.current) return;
    if (!urlModal) {
      if (viewProduct) setViewProduct(null);
      if (showWizard) { setShowWizard(false); setEditingProduct(null); }
      return;
    }
    if (urlModal === 'preview') {
      if (viewProduct) return;
      const p = products.find(x => x.id === urlProductId);
      if (!p || p.status !== 'active') { closeModal(); return; }
      axios.get(`${API}/weather-barrier-products-view/${p.id}`)
        .then(res => setViewProduct(res.data))
        .catch(() => { toast.error('Failed to load product'); closeModal(); });
    } else if (urlModal === 'edit') {
      if (showWizard) return;
      const p = products.find(x => x.id === urlProductId);
      if (!p) { closeModal(); return; }
      setEditingProduct(p);
      setShowWizard(true);
    }
  }, [urlModal, urlProductId, loading]);

  const csiItemId = csiItem?.id;

  const fetchProducts = useCallback(async () => {
    if (!projectId || !companyId) return;
    setLoading(true);
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}/weather-barrier-products/${projectId}${params}`);
      setProducts(res.data || []);
    } catch { toast.error('Failed to load weather barrier products'); }
    finally { setLoading(false); }
  }, [projectId, companyId, csiItemId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/weather-barrier-products/${projectId}/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); setConfirmState({ open: false }); }
  };

  // Bulk select
  const allSelected = products.length > 0 && selectedIds.size === products.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < products.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(products.map(p => p.id)));
  const toggleSelectRow = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    setBulkDeleting(true);
    try {
      await axios.post(`${API}/weather-barrier-products/${projectId}/bulk-delete`, { ids: [...selectedIds] });
      toast.success(`${selectedIds.size} product(s) deleted`);
      setSelectedIds(new Set());
      fetchProducts();
    } catch { toast.error('Bulk delete failed'); }
    finally { setBulkDeleting(false); setConfirmState({ open: false }); }
  };

  // Print
  const handleViewOrPrint = async (product, mode) => {
    isUserActionRef.current = true;
    openModal('preview', product.id);
    try {
      const res = await axios.get(`${API}/weather-barrier-products-view/${product.id}`);
      setViewProduct(res.data);
    } catch { toast.error('Failed to load product details'); closeModal(); }
    finally { isUserActionRef.current = false; }
  };

  const handleViewClose = () => {
    isUserActionRef.current = true;
    setViewProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleListDownloadPdf = async (product) => {
    setPdfLoading(product.id);
    try {
      const res = await axios.get(`${API}/weather-barrier-products-view/${product.id}`);
      setPdfProduct(res.data);
    } catch { toast.error('Failed to load product'); setPdfLoading(null); }
  };

  const handleModalDownloadPdf = async () => {
    if (!viewProduct) return;
    try {
      await generatePdfFromModalContent('wb-print-content-area', buildPdfFilename(viewProduct));
    } catch (err) {
      console.error('PDF modal download error:', err);
      toast.error('PDF download failed. Please try again.');
    }
  };

  useEffect(() => {
    if (!pdfProduct || !pdfRef.current) return;
    let cancelled = false;
    const gen = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled || !pdfRef.current) return;
      try {
        await generatePdfFromElement(pdfRef.current, buildPdfFilename(pdfProduct));
      } catch (err) {
        console.error('PDF list download error:', err);
        toast.error('PDF download failed. Please try again.');
      }
      setPdfProduct(null);
      setPdfLoading(null);
    };
    gen();
    return () => { cancelled = true; };
  }, [pdfProduct]);

  const handleEdit = (product) => {
    isUserActionRef.current = true;
    openModal('edit', product.id);
    setEditingProduct(product);
    setShowWizard(true);
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleWizardClose = () => {
    isUserActionRef.current = true;
    setShowWizard(false);
    setEditingProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const handleWizardComplete = () => {
    isUserActionRef.current = true;
    fetchProducts();
    setShowWizard(false);
    setEditingProduct(null);
    closeModal();
    setTimeout(() => { isUserActionRef.current = false; }, 0);
  };

  const triggerBrowserPrint = () => {
    const el = document.getElementById('wb-print-content-area');
    if (!el) return;
    const printWin = window.open('', '', 'width=800,height=900');
    if (!printWin) { toast.error('Pop-up blocked'); return; }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map(n => n.outerHTML).join('');
    printWin.document.write(`<!DOCTYPE html><html><head><title>Print</title>${styles}</head><body class="bg-white p-4">${el.innerHTML}</body></html>`);
    printWin.document.close();
    setTimeout(() => { printWin.print(); printWin.close(); }, 800);
  };

  const handleDownloadSubmittal = async (productId, submittalId, fileName, bytes) => {
    const url = `${API}/weather-barrier-products/${projectId}/${productId}/submittals/${submittalId}/download-url`;
    try {
      const res = await axios.get(url);
      if (res.data?.url) {
        startDownload(submittalId, res.data.url, fileName, bytes);
      }
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-4" data-testid="wb-section">
      {/* Share bar */}
      <ShareSection
        canEditCSIItems={canEditCSIItems} canManageProject={canManageProject}
        csiItem={csiItem} shareCompanySearch={shareCompanySearch}
        showCompanySuggestions={showCompanySuggestions} companySuggestions={companySuggestions}
        highlightedIndex={highlightedIndex} searchingCompanies={searchingCompanies}
        handleSearchCompanies={handleSearchCompanies} handleCompanySearchKeyDown={handleCompanySearchKeyDown}
        setShowCompanySuggestions={setShowCompanySuggestions} setHighlightedIndex={setHighlightedIndex}
        handleSelectCompany={handleSelectCompany} handleUnshareCSI={handleUnshareCSI}
      />

      {/* Filter bar */}
      <Division7FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} filterOptions={filterOptions} schema={schema} activeCount={activeCount} testIdPrefix="wb" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {canManageProject && (
            <div className="text-xs text-slate-600" data-testid="wb-share-info">
              Shared with: {csiItem?.assignedCompanyName ? (
                <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
              ) : (
                <span className="text-slate-400">N/A</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canManageProject && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmState({ open: true, title: 'Delete Selected', description: `Delete ${selectedIds.size} selected product(s)? This cannot be undone.`, onConfirm: handleBulkDelete })}
              disabled={bulkDeleting || selectedIds.size === 0}
              data-testid="wb-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete All{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
          {canManageProject && (
            <Button size="sm" onClick={() => { setEditingProduct(null); setShowWizard(true); }} data-testid="wb-add-product-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Create Product
            </Button>
          )}
          {canManageProject && csiItem && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShareModal(true)}
              data-testid="wb-share-btn"
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </Button>
          )}
        </div>
      </div>

      {/* Product table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg" data-testid="wb-empty-state">
          <p>{products.length === 0 ? 'No products created yet' : 'No products match the current filters'}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="wb-products-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                {canManageProject && (
                  <th className="w-10 px-3 py-3">
                    <OrangeCheckbox id="wb-select-all" checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} testId="wb-select-all-checkbox" />
                  </th>
                )}
                <th className="w-12 px-3 py-3 text-left font-semibold text-slate-700">Image</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Product Name / Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Country of Manufacturer</th>
                <th className="w-12 px-3 py-3 text-center font-semibold text-slate-700">QR</th>
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Status</th>
                <th className="w-32 px-3 py-3 text-center font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(p => {
                const isSelected = selectedIds.has(p.id);
                return (
                  <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-orange-50/50' : ''}`} data-testid={`wb-product-row-${p.id}`}>
                    {canManageProject && (
                      <td className="w-10 px-3 py-2">
                        <OrangeCheckbox id={`wb-select-row-${p.id}`} checked={isSelected} onChange={() => toggleSelectRow(p.id)} testId={`wb-select-row-${p.id}`} />
                      </td>
                    )}
                    <td className="w-12 px-3 py-2">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" data-testid={`wb-product-image-${p.id}`} />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center border border-slate-200" data-testid={`wb-product-image-placeholder-${p.id}`}>
                          <ImageIcon className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{p.manufacturerName || '-'}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{p.productNameModel}</td>
                    <td className="px-3 py-2 text-slate-600">{p.countryCode ? getCountryName(p.countryCode) : '-'}</td>
                    <td className="w-12 px-3 py-2 text-center" data-testid={`wb-qr-cell-${p.id}`}>
                      <button type="button" className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                        onClick={() => setQrModal({ open: true, qrToken: p.qrToken, productName: p.productNameModel })}
                        data-testid={`wb-qr-thumbnail-${p.id}`} title="Click to enlarge QR code"
                      >
                        <QRThumbnail qrToken={p.qrToken} size={32} />
                      </button>
                    </td>
                    <td className="w-20 px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${p.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'}`} data-testid={`wb-product-status-${p.id}`}>
                        {p.status || 'draft'}
                      </span>
                    </td>
                    <td className="w-32 px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handleViewOrPrint(p, 'print')} disabled={p.status !== 'active'} title={p.status === 'active' ? 'Preview' : 'Only active products can be previewed'} data-testid={`wb-print-btn-${p.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 transition-colors ${p.status === 'active' ? 'text-slate-400 hover:bg-orange-500 hover:text-white' : 'text-slate-300 cursor-not-allowed'}`} onClick={() => handleListDownloadPdf(p)} disabled={p.status !== 'active' || pdfLoading === p.id} title="Download PDF" data-testid={`wb-download-pdf-${p.id}`}>{pdfLoading === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</Button>
                        {canManageProject && (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => handleEdit(p)} title="Edit" data-testid={`wb-edit-btn-${p.id}`}><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors"
                              onClick={() => setConfirmState({ open: true, title: 'Delete Product', description: `Delete "${p.productNameModel}"? This cannot be undone.`, onConfirm: () => handleDelete(p.id) })}
                              disabled={deleting === p.id} title="Delete"
                              data-testid={`wb-delete-btn-${p.id}`}
                            >{deleting === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Wizard */}
      <AddWeatherBarrierProductWizard
        open={showWizard}
        onOpenChange={(v) => { if (!v) handleWizardClose(); }}
        onComplete={handleWizardComplete}
        onRefresh={fetchProducts}
        editProduct={editingProduct}
        companyId={companyId}
        projectId={projectId}
        csiItemId={csiItemId}
      />

      {/* Print Dialog */}
      {viewProduct && (
        <Dialog open={!!viewProduct} onOpenChange={() => handleViewClose()}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="wb-print-preview-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5" />
                Preview
              </DialogTitle>
              <DialogDescription>Review before printing</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto border rounded-lg" id="wb-print-content-area">
              <WeatherBarrierPrintViewInline product={viewProduct} project={project} user={user} showActions
                onPreviewSubmittal={(s) => {
                  axios.get(`${API}/weather-barrier-products/${viewProduct.projectId}/${viewProduct.id}/submittals/${s.id}/preview-url`).then(res => {
                    setPreviewSubmittal({ ...s, cloudinaryUrl: res.data?.url });
                  }).catch(() => toast.error('Failed to load preview'));
                }}
                onDownloadSubmittal={(sId, fName, bytes) => handleDownloadSubmittal(viewProduct.id, sId, fName, bytes)} />
            </div>
            <div className="flex justify-end gap-2 pt-3 shrink-0">
              <Button variant="outline" size="sm" onClick={() => handleViewClose()}>Close</Button>
              <Button variant="outline" size="sm" onClick={handleModalDownloadPdf} data-testid="wb-download-pdf-btn">
                <Download className="h-4 w-4 mr-2" />Download
              </Button>
              <Button size="sm" onClick={triggerBrowserPrint} data-testid="wb-print-confirm-btn">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />

      {/* Share Modal */}
      {csiItem && (
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="max-w-2xl" data-testid="wb-share-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Share This Specification
              </DialogTitle>
              <DialogDescription>
                Share {csiItem.itemTitle} specification with a sub-contractor company
              </DialogDescription>
            </DialogHeader>
            <ShareSection
              asModal
              tabValue=""
              inputIdPrefix="weather"
              csiItem={csiItem}
              project={project}
              user={user}
              shareCompanySearch={shareCompanySearch}
              showCompanySuggestions={showCompanySuggestions}
              companySuggestions={companySuggestions}
              highlightedIndex={highlightedIndex}
              searchingCompanies={searchingCompanies}
              handleSearchCompanies={handleSearchCompanies}
              handleCompanySearchKeyDown={handleCompanySearchKeyDown}
              setShowCompanySuggestions={setShowCompanySuggestions}
              setHighlightedIndex={setHighlightedIndex}
              handleSelectCompany={handleSelectCompany}
              handleUnshareCSI={handleUnshareCSI}
            />
          </DialogContent>
        </Dialog>
      )}

      <QRPreviewModal open={qrModal.open} onClose={() => setQrModal({ open: false, qrToken: null, productName: '' })} qrToken={qrModal.qrToken} productName={qrModal.productName} />
      <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />
      {pdfProduct && (
        <div style={{position:'fixed',left:'-10000px',top:'0px',width:'794px',background:'#fff',zIndex:-1,overflow:'visible',pointerEvents:'none'}} ref={pdfRef}>
          <WeatherBarrierPrintViewInline product={pdfProduct} project={pdfProduct.project} user={pdfProduct.viewedBy} />
        </div>
      )}
    </div>
  );
};

// WB Print View — full field coverage matching wizard Review Summary
export const WeatherBarrierPrintViewInline = ({ product, project, user, onDownloadSubmittal, onPreviewSubmittal, showActions }) => {
  if (!product) return null;
  const { QRCodeSVG } = require('qrcode.react');
  const { FileText, Download, Eye: EyeIcon } = require('lucide-react');
  const qrUrl = product.qrToken ? buildQrUrl(product.qrToken) : '';
  const proj = project || product.project || {};
  const usr = user || product.viewedBy || {};
  const subs = product.submittals || [];
  const fv = product.fieldVerification || {};
  const mp = product.materialProperties || {};
  const wr = product.waterResistance || {};
  const vp = product.vaporPermeability || {};
  const fc = product.fireCodeCompliance || {};
  const ip = product.installationParameters || {};
  const ic = product.interfaceContinuity || {};
  const de = product.durabilityExposure || {};

  const Section = ({ title, rows }) => (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {rows.filter(([, v]) => v).map(([label, value], i) => (
          <div key={i} className="flex flex-col sm:flex-row">
            <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
            <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="print-view bg-white p-4 sm:p-6 max-w-3xl mx-auto" data-testid="wb-print-view">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">{product.productNameModel}</h1>
          <p className="text-xs text-slate-500 mt-1">{product.manufacturerName || ''} {product.countryCode ? `| ${getCountryName(product.countryCode)}` : ''}</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium mt-2 ${product.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'}`}>{product.status || 'draft'}</span>
        </div>
        {qrUrl && (
          <div className="flex flex-col items-center ml-4 shrink-0" data-testid="wb-print-qr-code">
            <QRCodeSVG value={qrUrl} size={80} level="L" />
            <p className="text-[8px] text-slate-400 mt-1 text-center max-w-[80px] break-all">Specard QR</p>
          </div>
        )}
      </div>

      <Section title="Project Information" rows={[
        ['Project Name', proj.name],
        ['Project Code', proj.projectCode],
        ['Address', proj.address],
        ['Owner', proj.owner],
      ]} />

      <Section title="Weather Barrier Classification" rows={[
        ['WRB Type', product.wrbType],
        ['Scope Coverage', product.scopeCoverage],
      ]} />

      <Section title="Material & Physical Properties" rows={[
        ['Material Type', mp.materialType],
        ['Thickness', mp.thickness ? `${mp.thickness} in` : null],
        ['Reinforcement', mp.reinforcement],
        ['Surface Type', mp.surfaceType],
      ]} />

      <Section title="Water Resistance & Drainage" rows={[
        ['Water Resistance Test', wr.waterResistanceTest],
        ['Hydrostatic Pressure Rating (cm H₂O)', wr.hydrostaticPressureRating],
        ['Drainage Plane Capability', wr.drainagePlaneCapability],
        ['Integrated Drainage Gap (in.)', wr.integratedDrainageGap],
      ]} />

      <Section title="Vapor Permeability" rows={[
        ['Vapor Permeance', vp.vaporPermeance ? `${vp.vaporPermeance} perms` : null],
        ['ASTM E96 Method', vp.astmE96Method],
        ['Vapor Classification', vp.vaporClassification],
      ]} />

      <Section title="Fire & Code Compliance" rows={[
        ['NFPA 285 Compatibility', fc.nfpa285Compatibility
          ? <span className={`inline-flex items-center px-2 py-0.5 rounded ${fc.nfpa285Compatibility === 'Yes' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{fc.nfpa285Compatibility}</span>
          : null],
        ['ASTM E84', fc.astmE84],
        ['ICC-ES Report No', fc.iccEsReportNo],
        ['Exterior Wall Assembly Ref', fc.exteriorWallAssemblyRef],
      ]} />

      <Section title="Installation Parameters" rows={[
        ['Application Method', ip.applicationMethod],
        ['Substrate Type', ip.substrateType],
        ['Max Fastener Spacing', ip.maxFastenerSpacing],
        ['Lapping Requirements', ip.lappingRequirements ? `${ip.lappingRequirements} in` : null],
        ['Temp & Weather Limitations', ip.tempWeatherLimitations],
      ]} />

      <Section title="Interface & Continuity" rows={[
        ['Interfaces with', ic.interfacesWith],
        ['Transition Membrane Required?', ic.transitionMembraneRequired],
        ['Continuity Responsibility', ic.continuityResponsibility],
        ...(ic.continuityResponsibility === 'Other' ? [['Other Subcontractor', ic.otherSubcontractor]] : []),
      ]} />

      <Section title="Durability & Exposure Limits" rows={[
        ['UV Exposure Limit', de.uvExposureLimit ? `${de.uvExposureLimit} days` : null],
        ['Tear Resistance (lbf)', de.tearResistance],
        ['Repair Method', de.repairMethod],
        ['Temporary Exposure Allowed?', de.temporaryExposureAllowed],
      ]} />

      {subs.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals ({subs.length})</h3>
          <div className="space-y-1.5">
            {subs.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-2 text-xs" data-testid={`wb-print-submittal-${i}`}>
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0 print:text-gray-500" />
                <span className="text-slate-600 w-36 shrink-0">{s.documentType}</span>
                <span className="text-slate-800 flex-1 truncate">{s.fileName}</span>
                <span className="text-slate-400 shrink-0">{formatBytes(s.bytes)}</span>
                {showActions && onPreviewSubmittal && (
                  <button onClick={() => onPreviewSubmittal(s)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Preview" data-testid={`wb-print-preview-${i}`}>
                    <EyeIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                {showActions && onDownloadSubmittal && (
                  <button onClick={() => onDownloadSubmittal(s.id, s.fileName, s.bytes)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Download" data-testid={`wb-print-download-${i}`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Field Verification */}
      {(fv.installedThickness || fv.nonConformance || fv.inspectorComments || fv.installationPhotos?.length > 0) && (
        <div className="mb-4">
          <Section title="Field Verification" rows={[
            ['Installed Thickness', fv.installedThickness ? `${fv.installedThickness} in` : null],
            ['Non-conformance', fv.nonConformance ? (fv.nonConformance === 'Yes' ? 'Yes' : 'No') : null],
            ['Inspector Comments', fv.inspectorComments || null],
            ['Installation Photos', fv.installationPhotos?.length ? `${fv.installationPhotos.length} photo(s)` : null],
          ]} />
          {fv.installationPhotos?.length > 0 && (
            <div className="flex gap-2 mt-1 flex-wrap">
              {fv.installationPhotos.map((p, i) => (
                <img key={i} src={p.url} alt={p.fileName} className="w-20 h-20 object-cover rounded border" />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-3 mt-4 flex justify-between text-[10px] text-slate-400 print:text-gray-500">
        <span>Printed by: {usr.name || usr.email || 'Unknown'} ({usr.email || ''})</span>
        <span>{new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export default WeatherBarriersSection;
